import React, { useMemo } from 'react';
import styled from '@emotion/styled';
import type { Dayjs } from 'dayjs';
import type { ViewMode } from 'models/setup/PrjSchedule';
import { EVENT_CATEGORIES, type CategoryTheme } from 'utils/CalendarUtils';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import AddIcon from '@mui/icons-material/Add';
import type { Code } from 'models/common/CommonCode';
import type { SetupProject } from 'models/setup/SetupProject';
import { MultiComboBox } from 'components/selects/ComboBox';
import { useTranslation } from 'react-i18next';
import { SetupSchedule } from 'models/setup/SetupSchedule';
import useSessionStore from 'stores/useSessionStore';

const HeaderContainer = styled.div`
  display: flex;
  flex-direction: column;
  padding: 10px 16px;
  background-color: #ffffff;
  border-bottom: 1px solid #e2e8f0;
  flex-shrink: 0;
  user-select: none;
  gap: 10px;
  width: 100%;
  box-sizing: border-box;
`;

const TopRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  gap: 12px;
`;

const BottomRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  gap: 12px;
  flex-wrap: wrap;
`;

const LeftSection = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  flex-shrink: 0;
`;

const LeftControls = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const NavArrowBtn = styled.button`
  width: 32px;
  height: 32px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #dde0df;
  background: #ffffff;
  color: #0f172a;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    background: #f0f9ff;
    border-color: #0f172a;
  }
`;

const TodayBtn = styled.button`
  padding: 2px 14px;
  height: 32px;
  background: #ffffff;
  border: 1px solid #dde0df;
  font-size: 13px;
  font-weight: 600;
  color: #0f172a;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    background: #f0f9ff;
    border-color: #0f172a;
  }
`;

const CenterTitle = styled.h1`
  margin: 0;
  font-size: 18px;
  font-weight: 700;
  color: #0f172a;
  letter-spacing: -0.4px;
  text-align: left;
  white-space: nowrap;
`;

const FilterGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
`;

const FilterItem = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
`;

const FilterLabel = styled.span`
  font-size: 12px;
  font-weight: 600;
  color: #475569;
  white-space: nowrap;
`;

const FilterSelect = styled.select`
  height: 32px;
  padding: 0 8px;
  border: 1px solid #dde0df;
  font-size: 12px;
  color: #0f172a;
  background-color: #ffffff;
  box-sizing: border-box;
  outline: none;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    border-color: #94a3b8;
  }

  &:focus {
    border-color: #0f172a;
    box-shadow: 0 0 0 1px #1f7795;
  }
`;

const RightControls = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
`;

const ViewToggleGroup = styled.div`
  display: inline-flex;
  border: 1px solid #dde0df;
  overflow: hidden;
  background: #ffffff;
  height: 32px;
  box-sizing: border-box;
`;

const ViewToggleBtn = styled.button<{ active?: boolean }>`
  padding: 0 12px;
  height: 100%;
  border: none;
  background: ${({ active }) => (active ? '#1F7795' : '#ffffff')};
  color: ${({ active }) => (active ? '#ffffff' : '#0f172a')};
  font-size: 12.5px;
  font-weight: 600;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
  box-sizing: border-box;
  transition: all 0.12s ease;

  & + & {
    border-left: 1px solid #dde0df;
  }

  &:hover {
    background: ${({ active }) => (active ? '#1F7795' : '#f0f9ff')};
  }
`;

const FilterCheckboxItem = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
  margin-left: 6px;
  cursor: pointer;
  user-select: none;
`;

const FilterCheckbox = styled.input`
  width: 16px;
  height: 16px;
  accent-color: #1f7795;
  cursor: pointer;
  margin: 0;
`;

const CheckboxLabel = styled.label`
  font-size: 12px;
  font-weight: 600;
  color: #475569;
  cursor: pointer;
  white-space: nowrap;
`;

const AddEventBtn = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  height: 32px;
  padding: 0 12px;
  background: #ffffff;
  color: #0f172a;
  border: 1px solid #dde0df;
  font-size: 12.5px;
  font-weight: 600;
  cursor: pointer;
  box-sizing: border-box;
  line-height: 1;
  transition: all 0.15s ease;

  &:hover {
    background: #f0f9ff;
    border-color: #0f172a;
  }
`;

const LegendToggleGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
`;

const LegendItemChip = styled.button<{
  active: boolean;
  accentColor: string;
  bgColor: string;
}>`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  height: 28px;
  padding: 0 10px;
  border-radius: 14px;
  border: 1px solid ${({ active, accentColor }) => (active ? accentColor : '#cbd5e1')};
  background-color: ${({ active, bgColor }) => (active ? bgColor : '#ffffff')};
  color: ${({ active, accentColor }) => (active ? accentColor : '#94a3b8')};
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s ease;
  user-select: none;
  opacity: ${({ active }) => (active ? 1 : 0.55)};

  &:hover {
    filter: brightness(0.96);
    border-color: ${({ accentColor }) => accentColor};
    opacity: 1;
  }
`;

const LegendDot = styled.span<{ color: string; active: boolean }>`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: ${({ color, active }) => (active ? color : '#94a3b8')};
  flex-shrink: 0;
`;

const LegendSection = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
`;

const ToggleSwitchContainer = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  user-select: none;
  padding: 2px 2px;
  transition: opacity 0.15s ease;

  &:hover {
    opacity: 0.9;
  }
`;

const ToggleSwitchLabel = styled.span<{ checked?: boolean }>`
  font-size: 12.5px;
  font-weight: 600;
  color: ${({ checked }) => (checked ? '#1F7795' : '#334155')};
  transition: color 0.2s ease;
`;

const SwitchTrack = styled.div<{ checked?: boolean }>`
  position: relative;
  width: 38px;
  height: 20px;
  background-color: ${({ checked }) => (checked ? '#1F7795' : '#cbd5e1')};
  border-radius: 10px;
  transition: background-color 0.2s ease;
  display: flex;
  align-items: center;
  box-sizing: border-box;
`;

const SwitchCheck = styled.span<{ checked?: boolean }>`
  position: absolute;
  left: 6px;
  top: 50%;
  transform: translateY(-50%);
  color: #ffffff;
  font-size: 10px;
  font-weight: 800;
  line-height: 1;
  opacity: ${({ checked }) => (checked ? 1 : 0)};
  transition: opacity 0.15s ease;
  pointer-events: none;
`;

const SwitchKnob = styled.div<{ checked?: boolean }>`
  position: absolute;
  top: 2px;
  left: 2px;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background-color: #ffffff;
  border: 2px solid ${({ checked }) => (checked ? '#1F7795' : '#64748b')};
  box-sizing: border-box;
  transform: translateX(${({ checked }) => (checked ? '18px' : '0px')});
  transition: transform 0.2s cubic-bezier(0.4, 0, 0.2, 1), border-color 0.2s ease;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.15);
`;

interface CalendarHeaderProps {
  currentDate: Dayjs;
  viewMode: ViewMode;
  selectedCategories?: Set<string>;
  categories?: CategoryTheme[];
  onToggleCategory: (category: string) => void;
  onPrev: () => void;
  onNext: () => void;
  onToday: () => void;
  onViewModeChange: (mode: ViewMode) => void;
  onMoveList: () => void;
  onOpenAddModal: () => void;

  // 전체 펼치기/접기
  isExpanded?: boolean;
  onToggleExpand?: () => void;

  // 검색조건 (프로젝트, 협력업체, 유형)
  searchProject?: any;
  onSearchProjectChange?: (val: any) => void;
  searchExcp?: any;
  onsearchExcpChange?: (val: any) => void;
  searchType?: string;
  onSearchTypeChange?: (val: string) => void;
  searchDelay?: string;
  onSearchDelayChange: (val: string) => void;

  // 프로젝트 및 유형 목록
  projectList?: SetupProject[];
  excpList?: SetupSchedule[];
  typeList?: Code[];
}

export const CalendarHeader: React.FC<CalendarHeaderProps> = ({
  currentDate,
  viewMode,
  selectedCategories = new Set<string>(),
  categories = EVENT_CATEGORIES, // TODO 추후 SCHD_WKT_TP_CD 코드로 변경 예정(색상 설정 필요)
  onToggleCategory,
  onPrev,
  onNext,
  onToday,
  onViewModeChange,
  onMoveList,
  onOpenAddModal,
  isExpanded = false,
  onToggleExpand,
  searchProject,
  onSearchProjectChange,
  searchExcp,
  onsearchExcpChange,
  projectList = [],
  excpList = [],
  searchDelay,
  onSearchDelayChange,
}) => {
  const { t } = useTranslation();
  const { ensolYn, userId, deptCd, deptNm } = useSessionStore();

  const optionProject = useMemo(() => {
    return projectList.map((o) => ({ cmnCd: o.elmPrjId, cmnCdNm: o.elmPrjNm }));
  }, [projectList]);
  const optionExcp = useMemo(() => {
    return excpList.map((o) => ({ cmnCd: o.excpId, cmnCdNm: o.excpNm }));
  }, [excpList]);

  const handleProjectChange = (val: string[] = []) => {
    onSearchProjectChange?.(val);
  };
  const handleExcpChange = (val: string[] = []) => {
    onsearchExcpChange?.(val);
  };
  const handleDelayChange = (val: string) => {
    onSearchDelayChange?.(val);
  };

  const getHeaderTitle = () => {
    if (viewMode === 'month') {
      return currentDate.format('YYYY.MM');
    } else if (viewMode === 'week') {
      const start = currentDate.startOf('week');
      const end = currentDate.endOf('week');
      return `${start.format('YYYY.MM.DD')} ~ ${end.format('YYYY.MM.DD')}`;
    } else return currentDate.format('YYYY.M.DD (ddd)');
  };

  return (
    <HeaderContainer>
      {/* 1행: 날짜 이동 및 타이틀 (좌측) / 뷰 토글 및 액션 버튼 (우측) */}
      <TopRow>
        <LeftSection>
          <LeftControls>
            <NavArrowBtn onClick={onPrev} aria-label="이전">
              <ChevronLeftIcon sx={{ fontSize: 18 }} />
            </NavArrowBtn>
            <TodayBtn onClick={onToday}>{t('com.label.오늘', '오늘')}</TodayBtn>
            <NavArrowBtn onClick={onNext} aria-label="다음">
              <ChevronRightIcon sx={{ fontSize: 18 }} />
            </NavArrowBtn>
          </LeftControls>

          <CenterTitle>{getHeaderTitle()}</CenterTitle>
        </LeftSection>

        <RightControls>
          <ViewToggleGroup>
            <ViewToggleBtn active={viewMode === 'month'} onClick={() => onViewModeChange('month')}>
              {t('com.label.월', '월')}
            </ViewToggleBtn>
            <ViewToggleBtn active={viewMode === 'week'} onClick={() => onViewModeChange('week')}>
              {t('com.label.주', '주')}
            </ViewToggleBtn>
            <ViewToggleBtn active={viewMode === 'day'} onClick={() => onViewModeChange('day')}>
              {t('com.label.일', '일')}
            </ViewToggleBtn>
            <ViewToggleBtn onClick={onMoveList}>{t('com.label.검색', '검색')}</ViewToggleBtn>
          </ViewToggleGroup>

          <AddEventBtn onClick={onOpenAddModal}>
            <AddIcon sx={{ fontSize: 16 }} />
            {t('setup.label.일정 등록', '일정 등록')}
          </AddEventBtn>
        </RightControls>
      </TopRow>

      {/* 2행: 검색조건 및 범례 (아래줄 우측 정렬) */}
      <BottomRow>
        <FilterGroup>
          <FilterItem>
            <FilterLabel>{t('setup.label.프로젝트', '프로젝트')}</FilterLabel>
            <MultiComboBox
              id="elmPrjid"
              placeholder={String(t('com.label.전체', '전체'))}
              options={optionProject}
              defaultValue={searchProject}
              onChange={(value) => {
                handleProjectChange(value);
              }}
              style={{ width: '280px' }}
            />
          </FilterItem>
          <FilterItem>
            <FilterLabel>{t('setup.label.협력업체', '협력업체')}</FilterLabel>
            {ensolYn === 'Y' ? (
              <>
                <MultiComboBox
                  id="excpIds"
                  placeholder={String(t('com.label.전체', '전체'))}
                  options={optionExcp}
                  defaultValue={searchExcp}
                  onChange={(value) => {
                    handleExcpChange(value);
                  }}
                  style={{ width: '240px' }}
                />
              </>
            ) : (
              <div className="txt">
                <span>{deptNm}</span>
              </div>
            )}
          </FilterItem>
          {/* 1. 지연 조건 checkbox 추가 (유형 조건은 삭제) */}
          <FilterCheckboxItem>
            <CheckboxLabel htmlFor="delayed-only-check">
              {t('com.label.지연', '지연')}
            </CheckboxLabel>
            <FilterCheckbox
              id="delayed-only-check"
              type="checkbox"
              checked={searchDelay === 'Y'}
              onChange={(e) => handleDelayChange(e.target.checked ? 'Y' : '')}
            />
          </FilterCheckboxItem>
        </FilterGroup>

        {/* 펼치기/접기 On/Off 토글 스위치 및 업무 유형 범례 */}
        <LegendSection>
          {viewMode !== 'day' && (
            <ToggleSwitchContainer
              onClick={onToggleExpand}
              title={isExpanded ? '기본 보기로 접기' : '가려진 전체 일정 펼치기'}
            >
              <ToggleSwitchLabel checked={isExpanded}>
                {t('com.label.펼치기', '펼치기')}
              </ToggleSwitchLabel>
              <SwitchTrack checked={isExpanded}>
                <SwitchCheck checked={isExpanded}>✓</SwitchCheck>
                <SwitchKnob checked={isExpanded} />
              </SwitchTrack>
            </ToggleSwitchContainer>
          )}

          <LegendToggleGroup>
            {categories.map((cat) => {
              const isSelected = selectedCategories.has(cat.id);
              return (
                <LegendItemChip
                  key={cat.id}
                  type="button"
                  active={isSelected}
                  accentColor={cat.accent}
                  bgColor={cat.bg}
                  onClick={() => onToggleCategory(cat.id)}
                  title={`${cat.label} 일정 ${isSelected ? '숨기기/연하게' : '표시'}`}
                >
                  <LegendDot color={cat.accent} active={isSelected} />
                  {cat.label}
                </LegendItemChip>
              );
            })}
          </LegendToggleGroup>
        </LegendSection>
      </BottomRow>
    </HeaderContainer>
  );
};

export default CalendarHeader;
